'use client'

import clsx from 'clsx'
import { useLocale, useTranslations } from 'next-intl'
import {
  forwardRef,
  type MouseEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef
} from 'react'

import CloseIcon from '@/assets/icons/modal-close.svg'
import { Link } from '@/i18n/routing'

import type { NotificationItem } from '../../api/notificationsApi'
import { formatEventTime, formatEventTimeFull } from '../../lib/formatEventTime'
import { notificationHref } from '../../lib/notificationHref'
import { renderNotification } from '../../lib/renderNotification'
import { textDirection } from '../textDirection'
import { NotificationGlyph } from './NotificationGlyph'
import S from './NotificationsPanel.module.scss'

const SKELETON_ROWS = 3

export interface NotificationsPanelProps {
  id?: string
  items: NotificationItem[]
  isLoading?: boolean
  /** The feed could not be read. Shown instead of an empty centre. */
  isError?: boolean
  onRetry?: () => void
  /** There are older notifications the feed has not loaded yet. */
  hasMore?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
  /**
   * The fan pressed a row: the notification is read. A row with a
   * destination is a link and navigates by itself.
   */
  onOpen?: (item: NotificationItem, event: MouseEvent) => void
  /** The cross on a row: read this one without following its link. */
  onDismiss?: (item: NotificationItem) => void
  /** "Clear all": read every notification, loaded or not. */
  onClearAll?: () => void
}

const newestFirst = (a: NotificationItem, b: NotificationItem) =>
  (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0)

/**
 * The notification centre: unread notifications only, newest first, each
 * with its title, text, date and time. Loading, error and empty are states of
 * their own, so a failure never reads as "nothing new".
 */
export const NotificationsPanel = forwardRef<
  HTMLDivElement,
  NotificationsPanelProps
>(function NotificationsPanel(
  {
    id,
    items,
    isLoading = false,
    isError = false,
    onRetry,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    onOpen,
    onDismiss,
    onClearAll
  },
  ref
) {
  const t = useTranslations('notifications')
  const locale = useLocale()
  const headingId = useId()
  const scrollRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const refocusIndex = useRef<number | null>(null)

  const sorted = useMemo(() => [...items].sort(newestFirst), [items])

  // The panel scrolls inside itself, so the sentinel is watched against the
  // scroll area rather than the viewport: against the viewport it counts as
  // visible from the moment the panel opens and pulls every page at once.
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore || !onLoadMore || isLoadingMore) {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          onLoadMore()
        }
      },
      { root: scrollRef.current, rootMargin: '120px' }
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [hasMore, isLoadingMore, onLoadMore, items.length])

  // A row that was read without leaving the page disappears, and "clear all"
  // empties the list; focus goes to the row that took the place, or to the
  // dialog, instead of falling back to <body>.
  useLayoutEffect(() => {
    const index = refocusIndex.current
    if (index === null) {
      return
    }
    refocusIndex.current = null

    const controls = listRef.current?.querySelectorAll<HTMLElement>(
      '[data-notification-row]'
    )
    const next = controls?.[Math.min(index, controls.length - 1)]
    const fallback = scrollRef.current?.closest<HTMLElement>('[role="dialog"]')
    ;(next ?? fallback)?.focus({ preventScroll: true })
  }, [sorted])

  const text = (item: NotificationItem) =>
    renderNotification(
      item,
      (key, values) => t(key, values),
      key => t.has(key),
      locale
    )

  const renderRow = (item: NotificationItem, index: number) => {
    const { title, body } = text(item)
    const href = notificationHref(item.action_url)

    const content = (
      <>
        <span className={S.icon}>
          <NotificationGlyph type={item.type} />
        </span>
        <span className={S.text}>
          <span className={S.titleRow}>
            <span className={S.title}>{title}</span>
            <time
              className={S.time}
              dateTime={item.created_at}
              title={formatEventTimeFull(item.created_at, locale)}
            >
              {formatEventTime(item.created_at, locale)}
            </time>
          </span>
          {body ? <span className={S.body}>{body}</span> : null}
        </span>
      </>
    )

    return (
      <li key={item.id} className={clsx(S.item, onDismiss && S.dismissible)}>
        {href ? (
          <Link
            href={href}
            className={S.row}
            data-notification-row=""
            onClick={event => {
              refocusIndex.current = index
              onOpen?.(item, event)
            }}
            // A middle click opens a new tab without a click event.
            onAuxClick={event => {
              if (event.button === 1) {
                refocusIndex.current = index
                onOpen?.(item, event)
              }
            }}
          >
            {content}
          </Link>
        ) : (
          <button
            type="button"
            className={S.row}
            data-notification-row=""
            onClick={event => {
              refocusIndex.current = index
              onOpen?.(item, event)
            }}
          >
            {content}
          </button>
        )}
        {onDismiss ? (
          <button
            type="button"
            className={S.dismiss}
            aria-label={t('markRead')}
            title={t('markRead')}
            onClick={() => {
              refocusIndex.current = index
              onDismiss(item)
            }}
          >
            <CloseIcon aria-hidden />
          </button>
        ) : null}
      </li>
    )
  }

  let body: React.ReactNode

  if (isLoading && items.length === 0) {
    body = (
      <div className={S.rows} role="status" aria-busy="true">
        <span className={S.srOnly}>{t('loading')}</span>
        {Array.from({ length: SKELETON_ROWS }, (_, i) => (
          <span key={i} className={S.skeletonRow} aria-hidden />
        ))}
      </div>
    )
  } else if (isError && items.length === 0) {
    body = (
      <div className={S.state} role="alert">
        <span>{t('error')}</span>
        {onRetry ? (
          <button type="button" className={S.action} onClick={onRetry}>
            {t('retry')}
          </button>
        ) : null}
      </div>
    )
  } else if (items.length === 0) {
    body = (
      <div className={S.state}>
        <span className={S.stateIcon}>
          <NotificationGlyph size={22} />
        </span>
        <p className={S.stateText}>{t('empty')}</p>
      </div>
    )
  } else {
    body = (
      <>
        <ul className={S.rows} ref={listRef}>
          {sorted.map((item, index) => renderRow(item, index))}
        </ul>

        {hasMore ? (
          <div className={S.more} ref={sentinelRef}>
            {isLoadingMore ? (
              <span className={S.skeletonRow} aria-hidden />
            ) : onLoadMore ? (
              <button type="button" className={S.action} onClick={onLoadMore}>
                {t('loadMore')}
              </button>
            ) : null}
          </div>
        ) : null}

        {isError && onRetry ? (
          <div className={S.state} role="alert">
            <span>{t('error')}</span>
            <button type="button" className={S.action} onClick={onRetry}>
              {t('retry')}
            </button>
          </div>
        ) : null}
      </>
    )
  }

  return (
    <div
      ref={ref}
      id={id}
      className={S.panel}
      role="dialog"
      aria-labelledby={headingId}
      dir={textDirection(locale)}
      tabIndex={-1}
    >
      <div className={S.header}>
        <h2 id={headingId} className={S.heading}>
          {t('title')}
        </h2>
        {onClearAll && sorted.length > 0 ? (
          <button
            type="button"
            className={S.clearAll}
            onClick={() => {
              refocusIndex.current = 0
              onClearAll()
            }}
          >
            {t('clearAll')}
          </button>
        ) : null}
      </div>
      <div className={S.scroll} ref={scrollRef}>
        {body}
      </div>
    </div>
  )
})
